import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import {
  getDecisionNodeAtPath,
  extractCurrentMacroWizardPath,
  extractCurrentDecisionTreePath,
  isSpecialist as getIsSpecialist,
  extractMacroWizardLoading,
  extractSimplrTagsForSelectedTicket,
  extractRootDecisionTreeAtPath,
  extractSelectedTicketFromState,
  isAPartnerProtector,
} from '../../extractFromState';
import { decisionTreeCaseNotHandled } from '../../actions';
import { trackDecisionTreeStart } from '../../analytics/searchEventAnalytics';
import { DECISION_NODE_ACTIONS } from '../../constants/decisionTree';

import MacroWizard from '../MacroWizard/MacroWizard';
import { usePrevious } from '../../customHooks';
import { Wrapper } from './DecisionTree.styles';
import {
  hasSimplrSubmittedReachOut,
  hasPartnerRespondedToReachOut,
  partnerReachOutRetryNeeded,
} from 'util/ticketSelector';
import { Banner, BannerGreenCheckMark } from '../common/banner';
import LoadingComponent from '../LoadingComponent/LoadingComponent';
import ComponentMap from './DTMap';

const DecisionTree = () => {
  const dispatch = useDispatch();
  const tagPath = useSelector(extractCurrentMacroWizardPath);
  const decisionTree = useSelector(extractRootDecisionTreeAtPath);
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const decisionTreePath = useSelector(extractCurrentDecisionTreePath);
  const isSpecialist = useSelector(getIsSpecialist);
  const isPartnerProtector = useSelector(isAPartnerProtector);
  const loading = useSelector(extractMacroWizardLoading);
  const categoryRules = get(useSelector(extractSimplrTagsForSelectedTicket), 'categoryRules', {});

  const prevPropsDecisionNode = usePrevious(decisionNode);

  const escalationNodesActions = [
    DECISION_NODE_ACTIONS.ESCALATE_TO_SPECIALISTS,
    DECISION_NODE_ACTIONS.ESCALATE_TO_PARTNER,
    DECISION_NODE_ACTIONS.ESCALATE_TO_PARTNER_CUSTOM_DEPARTMENT,
  ];

  const shouldTicketBeEscalated = !decisionNode ? false : escalationNodesActions.includes(decisionNode.action);

  const ticket = useSelector(extractSelectedTicketFromState);

  useEffect(() => {
    if (decisionNode !== prevPropsDecisionNode && shouldTicketBeEscalated) {
      dispatch(decisionTreeCaseNotHandled('This case is not handled by Simplr.'));
    }
    // see if we are at the base of the decision tree
    if (decisionNode && !decisionTreePath) {
      trackDecisionTreeStart();
    }
  }, [decisionNode, decisionTreePath, dispatch, prevPropsDecisionNode, shouldTicketBeEscalated]);

  const renderComponent = () => {
    if (loading) {
      return <LoadingComponent marginTop={'0px'} />;
    }

    if (!decisionTree) {
      return <MacroWizard />;
    }

    const Node = (decisionNode && decisionNode.action && ComponentMap[decisionNode.action]) || ComponentMap.default;

    return <Node />;
  };

  const isOutOfScope = categoryRules[tagPath] === false;
  const isSpecialistOrPartnerProtector = isSpecialist || isPartnerProtector;

  return (
    <Wrapper>
      {isSpecialistOrPartnerProtector && isOutOfScope && (
        <Banner>
          <p>WARNING - This tag is out-of-scope</p>
        </Banner>
      )}
      {hasSimplrSubmittedReachOut(ticket) && (
        <Banner>
          <p>Partner reach out in progress</p>
        </Banner>
      )}
      {partnerReachOutRetryNeeded(ticket) && (
        <Banner>
          <p>There was a problem. Please try submitting again</p>
        </Banner>
      )}
      {hasPartnerRespondedToReachOut(ticket) && (
        <BannerGreenCheckMark>
          <p>The partner has responded</p>
        </BannerGreenCheckMark>
      )}
      {renderComponent()}
    </Wrapper>
  );
};

export default DecisionTree;
